import React, {FC, useCallback, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {useFormik} from 'formik'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import DataTable, {TableAction, TableColumn} from '../../../../shared/components/DataTable'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {formatDateTime} from '../shared/DocumentApi'
import {createTag, deleteTag, loadTags, updateTag} from './Api'
import {initialTagForm, ITagModel, ITagRequest, validationTag} from './TagModel'
import {TagChip} from './TagChips'
import {TagFormFields} from './TagFormFields'

/** Aday etiketleri: oluşturma, ad / renk düzenleme ve silme */
const TagPage: FC = () => {
  const [tags, setTags] = useState<ITagModel[]>([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState<ITagModel | null>(null)
  const [deletingItem, setDeletingItem] = useState<ITagModel | null>(null)

  const fetchTags = useCallback(async () => {
    setLoading(true)
    try {
      setTags((await loadTags(true)).data.payload)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Etiketler yüklenemedi'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTags()
  }, [fetchTags])

  const formik = useFormik<ITagRequest>({
    initialValues: initialTagForm,
    validationSchema: validationTag,
    onSubmit: async (values, {setSubmitting}) => {
      const request = {name: values.name.trim(), color: values.color}
      try {
        if (editingItem) {
          await updateTag(editingItem.id, request)
          showSuccessToast('Etiket güncellendi')
        } else {
          await createTag(request)
          showSuccessToast('Etiket oluşturuldu')
        }
        setShowForm(false)
        await fetchTags()
      } catch (error) {
        showErrorToast(apiErrorMessage(error, 'Etiket kaydedilemedi'))
      } finally {
        setSubmitting(false)
      }
    },
  })

  const openForm = (tag: ITagModel | null) => {
    setEditingItem(tag)
    formik.resetForm({values: tag ? {name: tag.name, color: tag.color} : initialTagForm})
    setShowForm(true)
  }

  const handleDelete = async () => {
    if (!deletingItem) return
    try {
      await deleteTag(deletingItem.id)
      showSuccessToast('Etiket silindi')
      await fetchTags()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Etiket silinemedi'))
    } finally {
      setDeletingItem(null)
    }
  }

  const columns: TableColumn<ITagModel>[] = [
    {key: 'name', label: 'Etiket', render: (_, tag) => <TagChip tag={tag} small={false} />},
    {
      key: 'candidateCount',
      label: 'Aday Sayısı',
      width: '120px',
      render: (count: number) => <span className='badge badge-light'>{count ?? 0}</span>,
    },
    {key: 'createdBy', label: 'Oluşturan', width: '160px', render: (value) => value || '-'},
    {key: 'createdDate', label: 'Oluşturulma', width: '160px', render: (value) => formatDateTime(value)},
  ]

  const actions: TableAction<ITagModel>[] = [
    {label: 'Düzenle', icon: 'bi bi-pencil', className: 'btn-light-primary me-2', onClick: (tag) => openForm(tag)},
    {label: 'Sil', icon: 'bi bi-trash', className: 'btn-light-danger', onClick: setDeletingItem},
  ]

  return (
    <>
      <PageTitle breadcrumbs={[]}>Etiketler</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Aday Etiketleri</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Başvurular, aday havuzu ve aday takip ekranlarında adayları etiketleyip etikete göre arayabilirsiniz
            </span>
          </h3>
          <div className='card-toolbar'>
            <button type='button' className='btn btn-sm btn-primary' onClick={() => openForm(null)}>
              <i className='bi bi-plus-lg fs-5'></i>
              Yeni Etiket
            </button>
          </div>
        </div>
        <div className='card-body pt-0'>
          <DataTable
            data={tags}
            columns={columns}
            actions={actions}
            showPagination={false}
            loading={loading}
            emptyMessage='Henüz etiket yok'
          />
        </div>
      </div>

      <Modal show={showForm} onHide={() => setShowForm(false)} dialogClassName='modal-dialog-centered' backdrop='static'>
        <form onSubmit={formik.handleSubmit} noValidate>
          <div className='modal-header'>
            <h5 className='modal-title'>{editingItem ? 'Etiketi Düzenle' : 'Yeni Etiket'}</h5>
            <button type='button' className='btn-close' onClick={() => setShowForm(false)} />
          </div>
          <div className='modal-body'>
            <TagFormFields
              autoFocus
              name={formik.values.name}
              color={formik.values.color}
              onNameChange={(name) => formik.setFieldValue('name', name)}
              onColorChange={(color) => formik.setFieldValue('color', color)}
              nameError={formik.submitCount > 0 ? formik.errors.name : undefined}
              colorError={formik.submitCount > 0 ? formik.errors.color : undefined}
            />
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light' onClick={() => setShowForm(false)}>
              İptal
            </button>
            <button type='submit' className='btn btn-primary' disabled={formik.isSubmitting}>
              {formik.isSubmitting ? <span className='spinner-border spinner-border-sm' /> : editingItem ? 'Güncelle' : 'Kaydet'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        show={!!deletingItem}
        header='Silme Onayı'
        text={`"${deletingItem?.name ?? ''}" etiketi silinecek ve ${deletingItem?.candidateCount ?? 0} adaydan kaldırılacak. Devam edilsin mi?`}
        handleClose={() => setDeletingItem(null)}
        applyOnClick={handleDelete}
      />

      <ToastContainer position='top-right' autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </>
  )
}

export {TagPage}
